import React, { useState, useRef, useEffect, useCallback } from "react";
import "./GstPartnerModal.css";
import { useSnackbar } from "../../../hooks/SnackbarProvider";
import { getRetailPartnerList, getRetailPartnerDetails } from "../../../../../../config/Services";
import { CircularProgress } from "@mui/material";

const GstPartnerModal = ({ isOpen, onClose, onConfirm, loading: externalLoading }) => {
  const [activeTab, setActiveTab] = useState("organization");
  const [searchQuery, setSearchQuery] = useState("");
  const [partnerList, setPartnerList] = useState([]);
  const [partnerListLoading, setPartnerListLoading] = useState(false);
  const [partnerListPage, setPartnerListPage] = useState(0);
  const [partnerListTotal, setPartnerListTotal] = useState(0);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerDetails, setPartnerDetails] = useState(null);
  const [partnerDetailsLoading, setPartnerDetailsLoading] = useState(false);
  const [partnerDetailsError, setPartnerDetailsError] = useState(false);
  const [gstNumber, setGstNumber] = useState("");
  const searchTimerRef = useRef(null);
  const showSnackbar = useSnackbar();

  const orgId = localStorage.getItem("orgId") || "";

  const fetchPartners = useCallback(
    (query, page, append = false) => {
      setPartnerListLoading(true);
      getRetailPartnerList({
        pageNumber: page,
        pageSize: 20,
        partnerId: orgId,
        startDate: "2020-01-01",
        endDate: "",
        partnerType: "RETAIL",
        ...(query ? { search: query } : {}),
      })
        .then((res) => {
          const content = res?.data?.data?.retails?.content || [];
          const total = res?.data?.data?.retails?.totalElements || 0;
          if (append) {
            setPartnerList((prev) => [...prev, ...content]);
          } else {
            setPartnerList(content);
          }
          setPartnerListTotal(total);
        })
        .catch((err) => {
          showSnackbar(err?.message || "Failed to load partners. Please retry.", "error");
        })
        .finally(() => {
          setPartnerListLoading(false);
        });
    },
    [orgId]
  );

  // Load initial list when tab becomes active
  useEffect(() => {
    if (isOpen && activeTab === "organization") {
      setPartnerListPage(0);
      fetchPartners("", 0);
    }
  }, [isOpen, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search
  useEffect(() => {
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      if (activeTab === "organization") {
        setPartnerListPage(0);
        setSelectedPartner(null);
        setPartnerDetails(null);
        fetchPartners(searchQuery, 0);
      }
    }, 500);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab("organization");
      setSearchQuery("");
      setPartnerList([]);
      setPartnerListPage(0);
      setPartnerListTotal(0);
      setSelectedPartner(null);
      setPartnerDetails(null);
      setPartnerDetailsError(false);
      setGstNumber("");
    }
  }, [isOpen]);

  const handleSelectPartner = (partner) => {
    if (selectedPartner?.retailId === partner?.retailId) {
      setSelectedPartner(null);
      setPartnerDetails(null);
      return;
    }
    setSelectedPartner(partner);
    setPartnerDetails(null);
    setPartnerDetailsError(false);
    setPartnerDetailsLoading(true);
    getRetailPartnerDetails(partner?.retailId)
      .then((res) => {
        const detail = res?.data?.data?.retail;
        if (!detail) {
          setPartnerDetailsError(true);
          showSnackbar("Partner details could not be loaded. Please retry.", "error");
        } else {
          setPartnerDetails(detail);
          setPartnerDetailsError(false);
        }
      })
      .catch((err) => {
        setPartnerDetailsError(true);
        showSnackbar(err?.message || "Failed to load partner details. Please retry.", "error");
      })
      .finally(() => {
        setPartnerDetailsLoading(false);
      });
  };

  const handleRetryPartnerDetails = () => {
    if (!selectedPartner) return;
    setPartnerDetails(null);
    setPartnerDetailsError(false);
    setPartnerDetailsLoading(true);
    getRetailPartnerDetails(selectedPartner?.retailId)
      .then((res) => {
        const detail = res?.data?.data?.retail;
        if (!detail) {
          setPartnerDetailsError(true);
          showSnackbar("Partner details could not be loaded. Please retry.", "error");
        } else {
          setPartnerDetails(detail);
          setPartnerDetailsError(false);
        }
      })
      .catch((err) => {
        setPartnerDetailsError(true);
        showSnackbar(err?.message || "Failed to load partner details. Please retry.", "error");
      })
      .finally(() => {
        setPartnerDetailsLoading(false);
      });
  };

  const handleLoadMore = () => {
    const nextPage = partnerListPage + 1;
    setPartnerListPage(nextPage);
    fetchPartners(searchQuery, nextPage, true);
  };

  const handleConfirm = () => {
    if (activeTab === "organization" && selectedPartner) {
      onConfirm({
        type: "ORGANIZATION",
        partner: selectedPartner,
        partnerDetails,
        gstNumber: null,
      });
    } else if (activeTab === "individual" && isValidGSTIN(gstNumber)) {
      onConfirm({
        type: "INDIVIDUAL",
        partner: null,
        partnerDetails: null,
        gstNumber: gstNumber.toUpperCase(),
      });
    }
  };

  // GSTIN format: 2-digit state code · 5-letter PAN · 4-digit PAN · 1-letter PAN · entity(1-9/A-Z) · Z · checksum
  const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
  const isValidGSTIN = (val) => GSTIN_REGEX.test(val);

  // Organization: confirm only when partner is selected AND details loaded successfully
  // Individual: confirm only when GSTIN passes format validation
  const canConfirm =
    (activeTab === "organization" &&
      selectedPartner &&
      !partnerDetailsLoading &&
      partnerDetails !== null &&
      !partnerDetailsError) ||
    (activeTab === "individual" && isValidGSTIN(gstNumber));

  if (!isOpen) return null;

  return (
    <>
      <div className="gpm-backdrop" onClick={onClose} />
      <div className="gpm-drawer" role="dialog" aria-modal="true" aria-label="GST Invoice">
        {/* ── Header ── */}
        <div className="gpm-header">
          <div className="gpm-header-left">
            <div className="gpm-header-icon">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" />
                <path d="M14 8H8" />
                <path d="M16 12H8" />
                <path d="M13 16H8" />
              </svg>
            </div>
            <h2 className="gpm-title">GST Invoice</h2>
          </div>
          <button
            className="gpm-close-btn"
            onClick={onClose}
            disabled={externalLoading}
            aria-label="Close"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        {/* ── Tabs ── */}
        <div className="gpm-tabs">
          <button
            className={`gpm-tab${activeTab === "organization" ? " active" : ""}`}
            onClick={() => setActiveTab("organization")}
            data-testid="gpm-tab-organization"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M3 9h18v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9Z" />
              <path d="m3 9 2.45-4.9A2 2 0 0 1 7.24 3h9.52a2 2 0 0 1 1.8 1.1L21 9" />
              <path d="M12 3v6" />
            </svg>
            Organization
          </button>
          <button
            className={`gpm-tab${activeTab === "individual" ? " active" : ""}`}
            onClick={() => setActiveTab("individual")}
            data-testid="gpm-tab-individual"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            Individual (GST no.)
          </button>
        </div>

        {/* ── Body ── */}
        <div className="gpm-body">
          {activeTab === "organization" ? (
            <div className="gpm-org-tab">
              {/* Search */}
              <div className="gpm-search-wrap">
                <svg
                  className="gpm-search-icon"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
                <input
                  className="gpm-search-input"
                  placeholder="Search by name, phone or email…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="gpm-search-input"
                />
                {partnerListLoading && partnerList?.length === 0 && (
                  <CircularProgress size={16} sx={{ color: "#2a71ff", flexShrink: 0 }} />
                )}
              </div>

              {/* Partner list */}
              <div className="gpm-partner-list">
                {partnerList?.length === 0 && !partnerListLoading ? (
                  <div className="gpm-empty-state">
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#94a3b8"
                      strokeWidth="1.5"
                    >
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                    <p className="gpm-empty-text">No retail partners found.</p>
                    <p className="gpm-empty-sub">Try a different search term.</p>
                  </div>
                ) : (
                  partnerList?.map((p) => {
                    const isSelected = selectedPartner?.retailId === p?.retailId;
                    return (
                      <button
                        key={p?.retailId}
                        className={`gpm-partner-card${isSelected ? " selected" : ""}`}
                        onClick={() => handleSelectPartner(p)}
                        data-testid={`gpm-partner-${p?.retailId}`}
                      >
                        <div className="gpm-partner-avatar">
                          {(p?.displayName || p?.name || "?")[0].toUpperCase()}
                        </div>
                        <div className="gpm-partner-info">
                          <p className="gpm-partner-name">{p?.displayName || p?.name || "—"}</p>
                          <p className="gpm-partner-sub">
                            {p?.phoneNumber || p?.email || p?.retailId}
                          </p>
                        </div>
                        {isSelected && (
                          <svg
                            className="gpm-partner-check"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#2a71ff"
                            strokeWidth="2.5"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </button>
                    );
                  })
                )}

                {partnerListLoading && partnerList?.length > 0 && (
                  <div className="gpm-list-spinner">
                    <CircularProgress size={20} sx={{ color: "#2a71ff" }} />
                  </div>
                )}

                {partnerList?.length > 0 &&
                  partnerList?.length < partnerListTotal &&
                  !partnerListLoading && (
                    <button className="gpm-load-more-btn" onClick={handleLoadMore}>
                      Load more ({partnerListTotal - partnerList?.length} remaining)
                    </button>
                  )}
              </div>

              {/* Partner detail card */}
              {selectedPartner && (
                <div className="gpm-detail-card" data-testid="gpm-partner-detail-card">
                  <div className="gpm-detail-card-header">
                    <p className="gpm-detail-card-title">Partner details</p>
                    {partnerDetailsLoading && (
                      <CircularProgress size={14} sx={{ color: "#2a71ff" }} />
                    )}
                  </div>
                  {partnerDetailsError ? (
                    <div className="gpm-detail-error" data-testid="gpm-partner-detail-error">
                      <p className="gpm-detail-error-msg">
                        Could not load partner details. Confirmation is unavailable until details
                        load successfully.
                      </p>
                      <button
                        className="gpm-detail-retry-btn"
                        onClick={handleRetryPartnerDetails}
                        disabled={partnerDetailsLoading}
                        data-testid="gpm-btn-retry-details"
                      >
                        {partnerDetailsLoading ? (
                          <CircularProgress size={12} sx={{ color: "#d97706" }} />
                        ) : (
                          "Retry"
                        )}
                      </button>
                    </div>
                  ) : partnerDetails ? (
                    <div className="gpm-detail-rows">
                      <div className="gpm-detail-row">
                        <span className="gpm-detail-label">Business name</span>
                        <span className="gpm-detail-val">
                          {partnerDetails?.displayName || partnerDetails?.customerName || "—"}
                        </span>
                      </div>
                      <div className="gpm-detail-row">
                        <span className="gpm-detail-label">GST number</span>
                        <span
                          className={`gpm-detail-val${
                            partnerDetails?.gstNumber
                              ? " gpm-detail-val--gst"
                              : " gpm-detail-val--muted"
                          }`}
                          data-testid="gpm-partner-gst-number"
                        >
                          {partnerDetails?.gstNumber || "Not available"}
                        </span>
                      </div>
                      {partnerDetails?.panNumber && (
                        <div className="gpm-detail-row">
                          <span className="gpm-detail-label">PAN</span>
                          <span className="gpm-detail-val">{partnerDetails?.panNumber}</span>
                        </div>
                      )}
                      {partnerDetails?.gstTreatment && (
                        <div className="gpm-detail-row">
                          <span className="gpm-detail-label">GST treatment</span>
                          <span className="gpm-detail-val">{partnerDetails?.gstTreatment}</span>
                        </div>
                      )}
                      {partnerDetails?.addresses?.[0] && (
                        <div className="gpm-detail-row">
                          <span className="gpm-detail-label">Address</span>
                          <span className="gpm-detail-val">
                            {[
                              partnerDetails?.addresses[0]?.addressLine1,
                              partnerDetails?.addresses[0]?.addressLine2,
                              partnerDetails?.addresses[0]?.city,
                              partnerDetails?.addresses[0]?.state,
                              partnerDetails?.addresses[0]?.pincode,
                            ]
                              .filter(Boolean)
                              .join(", ")}
                          </span>
                        </div>
                      )}
                      {partnerDetails?.paymentTerms && (
                        <div className="gpm-detail-row">
                          <span className="gpm-detail-label">Payment terms</span>
                          <span className="gpm-detail-val">{partnerDetails?.paymentTerms}</span>
                        </div>
                      )}
                      {partnerDetails?.taxPreference && (
                        <div className="gpm-detail-row">
                          <span className="gpm-detail-label">Tax preference</span>
                          <span className="gpm-detail-val">{partnerDetails?.taxPreference}</span>
                        </div>
                      )}
                    </div>
                  ) : !partnerDetailsLoading ? (
                    <p className="gpm-detail-loading">Loading details…</p>
                  ) : null}
                </div>
              )}
            </div>
          ) : (
            /* ── Individual tab ── */
            <div className="gpm-individual-tab">
              <div className="gpm-info-card">
                <div className="gpm-info-icon-wrap">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" />
                    <path d="M14 8H8" />
                    <path d="M16 12H8" />
                    <path d="M13 16H8" />
                  </svg>
                </div>
                <p className="gpm-info-text">
                  Enter the customer's 15-digit GSTIN to generate a GST invoice for this order.
                </p>
              </div>
              <div className="gpm-gst-input-group">
                <label className="gpm-gst-label">GSTIN (15 digits)</label>
                <input
                  className="gpm-gst-input"
                  placeholder="e.g. 29ABCDE1234F1Z5"
                  maxLength={15}
                  value={gstNumber}
                  onChange={(e) => setGstNumber(e.target.value.toUpperCase())}
                  data-testid="gpm-gst-input"
                />
                {gstNumber?.length > 0 && (
                  <p
                    className={`gpm-gst-count${
                      isValidGSTIN(gstNumber)
                        ? " complete"
                        : gstNumber?.length === 15
                        ? " invalid"
                        : ""
                    }`}
                    data-testid="gpm-gstin-hint"
                  >
                    {isValidGSTIN(gstNumber)
                      ? "✓ Valid GSTIN format"
                      : gstNumber?.length < 15
                      ? `${gstNumber?.length} / 15`
                      : "✗ Invalid GSTIN format — expected e.g. 29ABCDE1234F1Z5"}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="gpm-footer">
          <button
            className="gpm-btn-cancel"
            onClick={onClose}
            disabled={externalLoading}
            data-testid="gpm-btn-cancel"
          >
            Cancel
          </button>
          <button
            className="gpm-btn-confirm"
            disabled={!canConfirm || externalLoading}
            onClick={handleConfirm}
            data-testid="gpm-btn-confirm"
          >
            {externalLoading ? (
              <CircularProgress size={16} sx={{ color: "#fff" }} />
            ) : (
              "Confirm GST Order"
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default GstPartnerModal;
